import {
  Card,
  CircleLoader,
  IconButton,
  Space,
  WithTooltip,
} from '@procurify/ui'

import { PageActions } from '@webapp/core/components/Page'
import { useProcurifyIntl } from '@webapp/hooks'
import { ExpenseViewerRoles } from '@webapp/modules/expense/components/ExpenseItemTable'
import { EditExpenseLineItemDrawer } from '@webapp/modules/expense/feature-sets/EditExpenseLineItemDrawer'
import { ExpenseCommentPopoverButton } from '@webapp/modules/expense/feature-sets/ExpenseView/components'
import { ExpenseApprovalHistoryPresenter } from '@webapp/modules/expense/features/get-expense-approval-history/presenters/ExpenseApprovalHistoryPresenter'
import { ReviseExpenseReportPresenter } from '@webapp/modules/expense/features/revise-expense-report/presenters/ReviseExpenseReportPresenter'
import messages from './messages'
import { ExpenseReviseViewStyled } from './styles/ExpenseReviseViewStyled'
import { useExpenseReportReviseView } from './useExpenseReportReviseView'

export interface IExpenseReportReviseViewProps {
  uuid: string
}

export const ExpenseReportReviseView = ({
  uuid,
}: IExpenseReportReviseViewProps) => {
  const intl = useProcurifyIntl()

  const {
    data: {
      expense,
      isLoading,
      isReviseFormDefaultsLoading,
      initialValues,
      editingItem,
      drawerOpen,
    },
    states: {
      isRequester,
      showComments,
      expenseContentTypeId,
      userId,
      secondaryCategoryId,
    },
    methods: {
      onLineItemEditClick,
      onLineItemEditSuccess,
      onLineItemEditCancel,
      onReviseSuccess,
      onReviseCancel,
      onSubmittingChange,
      onSecondaryCategoryChange,
    },
    commands: { duplicateExpense },
  } = useExpenseReportReviseView({ uuid })

  if (isLoading) {
    return <CircleLoader />
  }

  const hasLogs = !!expense?.hasLogs

  return (
    <>
      <PageActions>
        <Space direction='horizontal'>
          {isRequester && (
            <WithTooltip
              title={intl.formatMessage(messages.duplicateToolbarButton)}
            >
              <IconButton
                icon='duplicate'
                aria-label={intl.formatMessage(messages.duplicateToolbarButton)}
                onClick={duplicateExpense}
              />
            </WithTooltip>
          )}

          {hasLogs && expense && (
            <ExpenseApprovalHistoryPresenter logs={expense.logs} />
          )}

          {showComments && expense && (
            <ExpenseCommentPopoverButton
              contentTypeId={expenseContentTypeId}
              objectId={expense.id}
              userId={userId}
            />
          )}
        </Space>
      </PageActions>

      <ExpenseReviseViewStyled>
        <Card>
          <Card.Content>
            <Space size='large'>
              {isReviseFormDefaultsLoading && <CircleLoader />}

              {expense && !isReviseFormDefaultsLoading && (
                <ReviseExpenseReportPresenter
                  expense={expense}
                  initialValues={initialValues}
                  onSuccess={onReviseSuccess}
                  onEditItem={onLineItemEditClick}
                  onSecondaryCategoryChange={onSecondaryCategoryChange}
                  onSubmittingChange={onSubmittingChange}
                  onCancel={onReviseCancel}
                  viewerRole={
                    isRequester
                      ? ExpenseViewerRoles.REQUESTER
                      : ExpenseViewerRoles.APPROVER
                  }
                />
              )}
            </Space>
          </Card.Content>
        </Card>
      </ExpenseReviseViewStyled>

      {editingItem && expense && (
        <EditExpenseLineItemDrawer
          drawerOpen={drawerOpen}
          onClose={onLineItemEditCancel}
          item={editingItem}
          expenseReportId={expense.id}
          requesterId={expense.requester.id}
          secondaryCategoryId={secondaryCategoryId}
          onSuccess={onLineItemEditSuccess}
        />
      )}
    </>
  )
}

ExpenseReportReviseView.displayName = 'ExpenseReportReviseView'
